import { ACCOUNT_TYPE, getPaymentDetails, selectUserData } from '../accountSlice'
import type { PaymentHistoryItem as Item } from './PaymentHistory'
import { useReduxDispatch, useReduxSelector } from 'store'
import useTranslation from 'hooks/useTranslation'
import { toLongDate, toTime } from 'utils/date'
import Skeleton from 'react-loading-skeleton'
import { displayPrice } from 'utils/string'
import Popup from 'components/popups/Popup'
import React, { useEffect } from 'react'
import Loading from 'components/Loading'
import Copy from 'components/Copy'

interface PaymentHistoryItemProps {
  id: string
}

const type = ACCOUNT_TYPE.payments
const PaymentHistoryItem: React.FC<PaymentHistoryItemProps> = ({ id }) => {
  const { data } = useReduxSelector(state => selectUserData(state, type))
  const item: Item | undefined = Array.isArray(data)
    ? data.find(item => item.id === id)
    : undefined
  const thereIsInvoiceList = Array.isArray(item?.invoices)
  const isActivePayment = item?.status === 'INIT' || item?.status === 'PENDING'

  const dispatch = useReduxDispatch()
  const { t, lang } = useTranslation('account')

  useEffect(() => {
    if (id && item?.invoices === null) {
      dispatch(getPaymentDetails(id))
    }
  }, [dispatch, id, item?.invoices])

  return (
    <Popup>
      <div className='popup--paymentHistory summary'>
        <div className='popup__title'>
          <div>{item?.title || item?.paymentMethodName}</div>
        </div>

        {item
          ? <div>
            <div className='account__list list'>
              <div>
                <div className='list__header element__title'>{t(item?.status === 'SUCCESS' ? 'paidAmount' : 'amount')}</div>
                <div className='list__element --big'>
                  <div>
                    <div className='element__title'>{displayPrice(item?.amount || 0)}</div>
                  </div>
                </div>
              </div>

              <div>
                <div className='list__header element__title'>{t('information')}</div>
                <div className='list__element --big'>
                  <div>
                    <div className='element__desc'>{t('buyer')}: <span>{item?.receiverCompanyName}</span></div>
                    {item?.receiverBankAccNumber && !isActivePayment && <div className='element__desc'>{t('accountNumber')}: <span>{item?.receiverBankAccNumber}</span></div>}
                    <div className='element__desc'>{t('date')}: <span>{toLongDate(item?.date, lang)}</span></div>
                    <div className='element__desc'>{t('time')}: <span>{toTime(item?.date, lang)}</span></div>
                    <div className='element__desc'>{t('paymentMethod')}: <span>{item?.paymentMethodName}</span></div>
                    <div className='element__desc'>{t('paymentStatus')}: <span>{t(`paymentStatuses.${item?.status}`)}</span></div>
                  </div>
                </div>
              </div>

              {isActivePayment && item?.paymentMethodKey === 'bank_transfer' && <div>
                <div className='list__header element__title'>{t('transferDetails')}</div>
                <div className='list__element --big'>
                  <div>
                    <div className='element__desc'>{t('buyer')}: <span>{item?.receiverCompanyName}</span> <Copy text={item?.receiverCompanyName} /></div>
                    {item?.receiverBankAccNumber && <div className='element__desc'>{t('accountNumber')}: <span>{item?.receiverBankAccNumber}</span> <Copy text={item?.receiverBankAccNumber} /></div>}
                    {item?.title && <div className='element__desc'>{t('title')}: <span>{item?.title}</span> <Copy text={item?.title} /></div>}
                  </div>
                </div>
              </div>}

              {thereIsInvoiceList
                ? item.invoices && item.invoices?.length > 0
                  ? <div>
                    <div className='list__header element__title'>{t('invoiceDetails')}</div>
                    {item.invoices?.map((invoice, idx) => <div
                      key={idx}
                      className='list__element --big'>
                      <div>
                        <div className='element__desc'>{t('invoiceNumber')}: {invoice.number}</div>
                        <div className='element__desc'>{t('amount')}: {displayPrice(invoice.amount)}</div>
                      </div>
                    </div>)}
                  </div>
                  : null
                : <div className='list__element --big --skeleton'>
                  <div className='group'>
                    <div>
                      <Skeleton className='skeleton__desc' />
                      <Skeleton className='skeleton__desc' />
                    </div>
                  </div>
                </div>}

              {thereIsInvoiceList
                ? item.invoices && item.invoices?.length > 0
                  ? <div>
                    <div className='list__header element__title'>{t('downloadDocument')}</div>
                    {item.invoices?.map((invoice, idx) => <div
                      key={idx}
                      className='list__element --big'>
                      <div className='group'>
                        <div>
                          <div className='element__title'>{t('invoiceNumber')} {invoice.number}</div>
                          <div className='element__desc'>{toLongDate(item.date, lang)} - {displayPrice(item.amount)}</div>
                        </div>
                        {invoice?.link && <i
                          className='icon icon--download'
                          onClick={() => {
                            window.open(invoice.link, '_blank')
                          }} />}
                      </div>
                    </div>)}
                  </div>
                  : null
                : <div className='list__element --big --skeleton'>
                  <div className='group'>
                    <div>
                      <Skeleton className='skeleton__title' />
                      <Skeleton className='skeleton__desc' />
                    </div>
                  </div>
                </div>}
            </div>
          </div>
          : <Loading />}
      </div>
    </Popup>
  )
}

export default PaymentHistoryItem